import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ApprovalCard from './ApprovalCard';
import DraftReviewModal from './DraftReviewModal';
import approvalService from '../../services/approvalService';
import './ApprovalQueue.css';

/**
 * Approval Queue Component
 * Main view for attorneys to review and approve AI-generated drafts
 * Supports ?task={taskId} URL param to auto-open a specific task for review
 */
function ApprovalQueue() {
  const { t } = useTranslation('attorney');
  const queryClient = useQueryClient();
  const [selectedTask, setSelectedTask] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('awaiting_approval');
  const [highlightedTaskId, setHighlightedTaskId] = useState(null);
  const highlightedRef = useRef(null);
  const initialLoadDone = useRef(false);

  // Read URL params on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const taskId = urlParams.get('task');
    if (taskId) {
      setHighlightedTaskId(taskId);
      setFilterType('all'); // Show all types to ensure we can find it
    }
  }, []);

  // Fetch approval queue using service layer
  const { data, isLoading, error } = useQuery({
    queryKey: ['approvalQueue', filterType, filterStatus],
    queryFn: () => approvalService.getApprovalQueue({
      agent: filterType !== 'all' ? filterType : undefined,
      status: filterStatus !== 'all' ? filterStatus : undefined
    })
  });

  // Extract tasks from response
  const tasks = data?.tasks || [];

  // Auto-open highlighted task when data loads
  useEffect(() => {
    if (highlightedTaskId && tasks.length > 0 && !initialLoadDone.current) {
      const task = tasks.find(t => t.id === highlightedTaskId);
      if (task) {
        setSelectedTask(task);
        initialLoadDone.current = true;
      }
      // Scroll to highlighted card
      if (highlightedRef.current) {
        highlightedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [highlightedTaskId, tasks]);

  // Approve mutation using service layer
  const approveMutation = useMutation({
    mutationFn: ({ taskId, modifications }) =>
      approvalService.approveTask(taskId, { modifications }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['approvalQueue'] });
      setSelectedTask(null);
    }
  });

  // Reject mutation using service layer
  const rejectMutation = useMutation({
    mutationFn: ({ taskId, reason }) =>
      approvalService.rejectTask(taskId, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['approvalQueue'] });
      setSelectedTask(null);
    }
  });

  const handleReview = (task) => {
    setSelectedTask(task);
  };

  const handleApprove = (modifications) => {
    if (selectedTask) {
      approveMutation.mutate({
        taskId: selectedTask.id,
        modifications
      });
    }
  };

  const handleReject = (reason) => {
    if (selectedTask) {
      rejectMutation.mutate({
        taskId: selectedTask.id,
        reason
      });
    }
  };

  if (isLoading) {
    return (
      <div className="approval-queue">
        <div className="approval-queue-loading">
          {t('attorney.approvalQueue.loading', 'Loading approval queue...')}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="approval-queue">
        <div className="approval-queue-error">
          {t('attorney.approvalQueue.error', 'Error loading approval queue')}
        </div>
      </div>
    );
  }

  return (
    <div className="approval-queue">
      <div className="approval-queue-header">
        <h1>{t('attorney.approvalQueue.title', 'Approval Queue')}</h1>

        <div className="approval-queue-filters">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="approval-filter-select"
          >
            <option value="awaiting_approval">
              {t('attorney.approvalQueue.statusPending', 'Pending')}
            </option>
            <option value="approved">
              {t('attorney.approvalQueue.statusApproved', 'Approved')}
            </option>
            <option value="rejected">
              {t('attorney.approvalQueue.statusRejected', 'Rejected')}
            </option>
            <option value="all">
              {t('attorney.approvalQueue.statusAll', 'All Status')}
            </option>
          </select>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="approval-filter-select"
          >
            <option value="all">
              {t('attorney.approvalQueue.allTypes', 'All Types')}
            </option>
            <option value="office_action_response">
              {t('attorney.approvalQueue.officeAction', 'Office Action Responses')}
            </option>
            <option value="opposition_response">
              {t('attorney.approvalQueue.opposition', 'Opposition Responses')}
            </option>
            <option value="renewal">
              {t('attorney.approvalQueue.renewal', 'Renewal Applications')}
            </option>
          </select>

          <button
            className="approval-refresh-btn"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['approvalQueue'] })}
          >
            {t('attorney.approvalQueue.refresh', 'Refresh')}
          </button>
        </div>
      </div>

      <div className="approval-queue-content">
        {tasks.length === 0 ? (
          <div className="approval-queue-empty">
            <span className="approval-queue-empty-icon">
              {filterStatus === 'awaiting_approval' ? '✅' : '📋'}
            </span>
            <p>
              {filterStatus === 'awaiting_approval'
                ? t('attorney.approvalQueue.empty', 'No tasks awaiting approval')
                : filterStatus === 'approved'
                ? t('attorney.approvalQueue.emptyApproved', 'No approved tasks found')
                : filterStatus === 'rejected'
                ? t('attorney.approvalQueue.emptyRejected', 'No rejected tasks found')
                : t('attorney.approvalQueue.emptyAll', 'No tasks found')
              }
            </p>
          </div>
        ) : (
          <div className="approval-cards">
            {tasks.map(task => {
              const isHighlighted = task.id === highlightedTaskId;
              return (
                <div key={task.id} ref={isHighlighted ? highlightedRef : null}>
                  <ApprovalCard
                    task={task}
                    onReview={handleReview}
                    isHighlighted={isHighlighted}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedTask && (
        <DraftReviewModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          isApproving={approveMutation.isPending}
          isRejecting={rejectMutation.isPending}
        />
      )}
    </div>
  );
}

export default ApprovalQueue;
